import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@/features/auth/useAuthErrorMessage';
import { useUpdatePost } from '@/features/feed/useFeed';
import { MentionInput } from '@/features/mentions/MentionInput';
import { toDisplayText } from '@/features/mentions/mention-token';
import type { Post } from '@/lib/schemas/post';

const TITLE_LIMIT = 120;
const BODY_LIMIT = 5000;

interface EditPostDialogProps {
  post: Post;
  open: boolean;
  onClose: () => void;
}

/**
 * Fixing a typo, not rewriting history.
 *
 * Deliberately narrower than the composer: the register, the place and the
 * media are fixed once a post exists, because changing what a post *is* after
 * people have replied to it changes the meaning of their replies. The API
 * enforces the same, and closes the window after thirty minutes.
 */
export function EditPostDialog({ post, open, onClose }: EditPostDialogProps) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const update = useUpdatePost();

  const [title, setTitle] = useState(post.title ?? '');
  const [body, setBody] = useState(post.body);

  const changed = title !== (post.title ?? '') || body !== post.body;
  // Counted as written, not as stored: the mention markup is longer than the
  // name it prints, and a counter that jumps by forty on tagging somebody
  // reads as a bug.
  const written = toDisplayText(body);
  const tooLong = body.length > BODY_LIMIT || title.length > TITLE_LIMIT;

  const submit = (): void => {
    update.mutate(
      { id: post.id, title, body },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('post.edit.title')}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {update.isError && (
            <Alert severity="error">{errorMessage(update.error, t('post.edit.failed'))}</Alert>
          )}

          <TextField
            label={t('composer.titleOptional')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            error={title.length > TITLE_LIMIT}
            helperText={`${title.length}/${TITLE_LIMIT}`}
            fullWidth
          />

          <MentionInput
            label={t('composer.bodyLabel')}
            value={body}
            onChange={setBody}
            multiline
            minRows={5}
            error={body.length > BODY_LIMIT}
            helperText={`${written.length}/${BODY_LIMIT} · ${t('mentions.hint')}`}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={!changed || tooLong || body.trim().length === 0 || update.isPending}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
